.wrapper {
  position: relative;
}

.root {
  align-items: center;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  height: 40px;
  outline: none;
  padding: 0 12px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  width: 100%;

  &:hover {
    border-color: var(--color-foreground);
  }
}

.root-size-sm {
  font-size: 13px;
  height: 32px;
}

// Match the single-select `Select` open state — a blue focus ring rather than
// a heavy black border, so the two filter controls look consistent when open.
.root-is-open {
  border-color: var(--color-focus);
  box-shadow: 0 0 0 2px var(--color-focus);
}

.root-is-disabled {
  background: var(--color-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.value {
  color: var(--color-foreground);
  flex: 1;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.placeholder {
  color: var(--color-foreground);
  flex: 1;
  // Match `.value` — never wrap the trigger label to a second line when the
  // control is narrow; truncate with an ellipsis instead (the dropdown still
  // shows the full option text).
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leadingIcon {
  align-items: center;
  color: var(--color-foreground);
  display: inline-flex;
  flex-shrink: 0;
}

.clearAll {
  color: var(--color-muted);
  cursor: pointer;
  flex-shrink: 0;
  height: 16px;
  transition: color 0.15s;
  width: 16px;

  &:hover {
    color: var(--color-foreground);
  }
}

.chevron {
  color: var(--color-muted);
  flex-shrink: 0;
  height: 14px;
  transition: transform 0.15s;
  width: 14px;
}

.chevronOpen {
  transform: rotate(180deg);
}

/* --- Dropdown --- */

.dropdown {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgb(0 0 0 / 10%);
  display: flex;
  flex-direction: column;
  left: 0;
  max-height: 320px;
  // Free-text options (e.g. tour-operator specials) can be long. Cap the
  // dropdown so it never exceeds the trigger's column or punches out of
  // the viewport; option labels wrap inside (`.option-label`).
  max-width: min(320px, calc(100vw - 24px));
  min-width: 100%;
  overflow: hidden;
  padding: 4px;
  position: absolute;
  top: calc(100% + 4px);
  width: max-content;
  z-index: 50;
}

.searchRow {
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  gap: 6px;
  margin: -4px -4px 4px;
  padding: 8px 10px;
}

.searchIcon {
  color: var(--color-muted);
  flex-shrink: 0;
  height: 14px;
  width: 14px;
}

.searchInput {
  background: transparent;
  border: none;
  color: var(--color-foreground);
  flex: 1;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  padding: 0;

  &::placeholder {
    color: var(--color-placeholder);
  }
}

.optionList {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.option {
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--color-foreground);
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 14px;
  gap: 8px;
  padding: 6px 8px;
  text-align: left;
  transition: background-color 0.1s;
  width: 100%;

  &:hover {
    background: var(--color-secondary);
  }
}

.option-child {
  // Indented second-level row (e.g. a subcategory under a selected category).
  padding-left: 34px;
}

.optionLabel {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
  word-break: break-word;
}

.optionCount {
  color: var(--color-muted);
  flex-shrink: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  margin-left: auto;
  padding-left: 8px;
}

.optionDot {
  border-radius: 9999px;
  display: inline-block;
  flex-shrink: 0;
  height: 10px;
  width: 10px;
}

// Option glyph (e.g. category icon) — muted, 16px, like the legacy
// faceted-filter's `text-muted-foreground h-4 w-4` treatment.
.optionIcon {
  align-items: center;
  color: var(--color-muted, #6b7280);
  display: inline-flex;
  flex-shrink: 0;

  svg {
    height: 16px;
    width: 16px;
  }
}

.checkbox {
  align-items: center;
  background: var(--color-background);
  border: 1.5px solid var(--color-focus);
  border-radius: 4px;
  display: inline-flex;
  flex-shrink: 0;
  height: 16px;
  justify-content: center;
  opacity: 0.5;
  width: 16px;
}

.checkbox-checked {
  background: var(--color-focus);
  border-color: var(--color-focus);
  opacity: 1;
}

.checkIcon {
  color: var(--color-background);
  height: 12px;
  stroke-width: 3;
  width: 12px;
}

.empty {
  color: var(--color-muted);
  display: block;
  font-size: 13px;
  padding: 12px;
  text-align: center;
}

.dropdown-portal {
  // Position is driven by inline `left`/`right`/`top`/`bottom`
  // (trigger-relative, viewport-fixed). Reset the inline-absolute defaults so
  // they don't fight.
  left: auto;
  right: auto;
  top: auto;
  // Above the Modal overlay (z-index: 2000) so a portaled dropdown opened
  // from inside a modal renders in front of it, not behind.
  z-index: 2100;
}
